import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Checkbox, Form } from 'antd';
import { FieldData } from 'rc-field-form/es/interface';
import { IAntDFormChangeFieldName, IEditForm, IFormFields } from '../../../interfaces';
import { useProfileFeatureFlags, useProfileIsRight, useSubscriberInfo } from '../../../hooks';
import {
    apiAddFormErrorMessages,
    apiMessagesDeleteMessageByFieldName,
    apiMessagesGetFormRules,
    apiMessagesGetFormRulesFieldBlank,
    patchSubscriber,
} from '../../../middleware';
import { tF } from '../../../translate';
import {
    CardInfoToolBar,
    CardMainLayout,
    Catcher,
    FormMultiCheckboxFeaturesFields,
    InputDebounce,
} from '../../../Components';

export const EditSubscriberForm: React.FC<IEditForm> = observer(({ setEditMode }: IEditForm) => {
    const isSubscribersFeatures = useProfileFeatureFlags()?.subscribersFeatures;
    const isLord = useProfileIsRight('LORD');
    const [form] = Form.useForm();
    const initialValues = useSubscriberInfo();
    const isNew = !initialValues.id;
    const [isSending, setSending] = useState(false);
    const [isChanged, setChanged] = useState(false);

    useEffect((): void => {
        if (!isChanged) {
            form.setFieldsValue(initialValues);
        }
    }, [initialValues]);

    const onFieldsChange = (changedFields: FieldData[]): void => {
        const fieldNameChange =
            changedFields && changedFields[0] && (changedFields[0].name as IAntDFormChangeFieldName);
        if (fieldNameChange) {
            apiMessagesDeleteMessageByFieldName(fieldNameChange);
        }

        setChanged(true);
    };

    const onFinish = async (fields: IFormFields): Promise<void> => {
        setSending(true);
        const { isError, resp } = await patchSubscriber(initialValues.id, { ...fields });

        if (isError) {
            apiAddFormErrorMessages(resp || {}, form);
        } else {
            setEditMode && setEditMode(false);
        }
        setSending(false);
    };

    const imsiTitle = tF('IMSI');
    const roamingImsiTitle = tF('Roaming');
    const iccidTitle = tF('ICCID');
    const msisdnTitle = tF('MSISDN');

    return (
        <Catcher>
            <CardMainLayout idSpinners={['GET_SUBSCRIBER_V2', 'GET_OPERATOR']} isEdit name={initialValues.imsi}>
                <Form
                    form={form}
                    name="subscriberInfo"
                    initialValues={initialValues}
                    onFieldsChange={onFieldsChange}
                    onFinish={onFinish}
                    scrollToFirstError={true}
                    layout={'vertical'}
                >
                    <Form.Item name="imsi" label={imsiTitle} rules={apiMessagesGetFormRulesFieldBlank()}>
                        <InputDebounce placeholder={imsiTitle} isFirstInput />
                    </Form.Item>
                    <Form.Item name="roamingImsi" label={roamingImsiTitle} rules={apiMessagesGetFormRules}>
                        <InputDebounce placeholder={roamingImsiTitle} />
                    </Form.Item>
                    <Form.Item name="iccId" label={iccidTitle} rules={apiMessagesGetFormRules}>
                        <InputDebounce placeholder={iccidTitle} />
                    </Form.Item>
                    <Form.Item name="msisdn" label={msisdnTitle} rules={apiMessagesGetFormRules}>
                        <InputDebounce placeholder={msisdnTitle} />
                    </Form.Item>
                    {isLord ? (
                        <Form.Item name="onPremMigrated" valuePropName="checked" rules={apiMessagesGetFormRules}>
                            <Checkbox>{tF('Migrate on prem')}</Checkbox>
                        </Form.Item>
                    ) : null}
                    {isSubscribersFeatures ? <FormMultiCheckboxFeaturesFields /> : null}
                    <CardInfoToolBar isLoading={isSending} isNew={isNew} isSubmitButton />
                </Form>
            </CardMainLayout>
        </Catcher>
    );
});
